import { parseTunables } from '@studio/shared';

/**
 * Plain-English annotations for generated game code.
 *
 * The point of showing a child the code is to stop it being magic. That only
 * works if the code is legible, so each recognised construct gets one sentence
 * in words a nine-year-old knows.
 *
 * Rule-based on purpose rather than asking a model: it is instant, free, and
 * identical every time — a child re-reading the same line gets the same
 * explanation, which is how you learn a thing.
 */

export type Annotation = { line: number; text: string };

type Rule = { pattern: RegExp; text: string };

const RULES: Rule[] = [
  {
    pattern: /requestAnimationFrame/,
    text: 'This runs the game over and over — about 60 times every second.',
  },
  { pattern: /getContext\s*\(\s*['"]2d/, text: 'This gets the drawing board ready.' },
  { pattern: /addEventListener\s*\(\s*['"]keydown/, text: 'This waits for you to press a key.' },
  { pattern: /addEventListener\s*\(\s*['"]keyup/, text: 'This notices when you let a key go.' },
  { pattern: /addEventListener\s*\(\s*['"]pointer/, text: 'This waits for a finger or a mouse.' },
  { pattern: /function\s+update|const\s+update\s*=/, text: 'Everything that MOVES gets moved here.' },
  { pattern: /function\s+draw|const\s+draw\s*=/, text: 'Everything you can SEE gets drawn here.' },
  { pattern: /function\s+loop|const\s+loop\s*=/, text: 'This is the heartbeat: move everything, draw everything, again.' },
  { pattern: /\.fillRect\s*\(/, text: 'This draws a solid rectangle.' },
  { pattern: /\.arc\s*\(/, text: 'This draws a circle.' },
  { pattern: /\.fillText\s*\(/, text: 'This writes words on the screen.' },
  { pattern: /fillStyle\s*=/, text: 'This picks the colour for whatever gets drawn next.' },
  { pattern: /Math\.random\s*\(/, text: "This picks a random number, so it's different every time you play." },
  { pattern: /Math\.hypot\s*\(/, text: 'This measures the distance between two things.' },
  { pattern: /Math\.(min|max)\s*\(/, text: 'This keeps a number from going too far one way.' },
  { pattern: /Studio\.win\s*\(/, text: 'This tells the studio you won.' },
  { pattern: /Studio\.lose\s*\(/, text: 'This tells the studio the game is over.' },
  { pattern: /Studio\.score\s*\(/, text: 'This tells the studio your new score.' },
  { pattern: /\.vy\s*\+=|vy\s*\+=\s*GRAVITY/, text: 'This is gravity pulling things down a bit more each moment.' },
  { pattern: /\bfor\s*\(\s*(?:const|let)\s+\w+\s+of\b/, text: 'This does the same thing to every one of them, one at a time.' },
];

/**
 * Annotate a document. At most one annotation per line — the most specific rule
 * wins, because a wall of notes is as unreadable as no notes at all.
 */
export function annotate(html: string): Annotation[] {
  const annotations = new Map<number, string>();

  // Tunables first: a line a child can actually change is the most useful thing
  // to point at, and outranks any generic construct on the same line.
  for (const tunable of parseTunables(html)) {
    annotations.set(tunable.line, `You can change this — ${tunable.label}.`);
  }

  html.split('\n').forEach((line, index) => {
    const lineNumber = index + 1;
    if (annotations.has(lineNumber)) return;
    if (/^\s*(\/\/|\*|<!--)/.test(line)) return; // already a comment
    const rule = RULES.find((r) => r.pattern.test(line));
    if (rule) annotations.set(lineNumber, rule.text);
  });

  return [...annotations.entries()]
    .map(([line, text]) => ({ line, text }))
    .sort((a, b) => a.line - b.line);
}

/** Just the script, so the panel does not open on forty lines of CSS. */
export function extractScript(html: string): { code: string; offset: number } {
  const match = /<script(?![^>]*\bsrc\b)[^>]*>([\s\S]*?)<\/script>/i.exec(html);
  if (!match) return { code: html, offset: 0 };
  const offset = html.slice(0, match.index).split('\n').length;
  // Strip the newline after <script> and the one before </script>, so every
  // rendered line corresponds to a real line of the document and the panel does
  // not end on a phantom blank row.
  const code = match[1]!.replace(/^\n/, '').replace(/\n[ \t]*$/, '');
  return { code, offset };
}

/**
 * Map a line of the extracted script back to its line number in the whole
 * document.
 *
 * `offset` is the document line the `<script>` TAG sits on, so the script's
 * first line of code is the line AFTER it — hence the +1. Getting this wrong
 * does not break anything visibly: annotations and highlights still appear,
 * just against the neighbouring line. For a child using the highlight to work
 * out which line does what, that is worse than showing nothing at all.
 */
export const documentLineFor = (offset: number, scriptIndex: number): number =>
  offset + scriptIndex + 1;
